import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ParticleField from "./ParticleField";

const DEFAULT_STORY = [
  "Defense protocol activated.",
  "Grid initialized.",
  "Consumption detected.",
  "Neural pathways expanding.",
  "Speed increasing.",
  "Instability rising.",
  "Containment failing.",
  "Reality desync detected.",
  "You are not playing the system.",
  "You ARE the system."
];

const BASE_TILES = 28;

function getGrid(reservedBottom = 0) {
  const W = window.innerWidth;
  const H = window.innerHeight - reservedBottom;
  const tileSize = Math.floor(Math.min(W, H) / BASE_TILES);
  const TILES_X = Math.ceil(W / tileSize);
  const TILES_Y = Math.ceil(H / tileSize);
  return { tileSize, TILES_X, TILES_Y, W, H };
}

// Vibration helper — silently fails on unsupported devices
const vibrate = (pattern) => {
  try {
    if (navigator.vibrate) navigator.vibrate(pattern);
  } catch (e) {}
};

const GridBackground = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const TILE = Math.floor(Math.min(window.innerWidth, window.innerHeight) / BASE_TILES);
    let frame = 0; let raf;
    const draw = () => {
      raf = requestAnimationFrame(draw); frame++;
      const W = canvas.width; const H = canvas.height;
      ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(0,245,255,0.04)"; ctx.lineWidth = 0.5;
      const cols = Math.ceil(W / TILE); const rows = Math.ceil(H / TILE);
      for (let i = 0; i <= cols; i++) { ctx.beginPath(); ctx.moveTo(i * TILE, 0); ctx.lineTo(i * TILE, H); ctx.stroke(); }
      for (let j = 0; j <= rows; j++) { ctx.beginPath(); ctx.moveTo(0, j * TILE); ctx.lineTo(W, j * TILE); ctx.stroke(); }
      if (frame % 8 === 0) {
        const rx = Math.floor(Math.random() * cols) * TILE;
        const ry = Math.floor(Math.random() * rows) * TILE;
        ctx.fillStyle = `rgba(0,255,136,${Math.random() * 0.07})`;
        ctx.fillRect(rx + 1, ry + 1, TILE - 2, TILE - 2);
      }
      if (frame % 23 === 0) {
        const rx2 = Math.floor(Math.random() * cols) * TILE;
        const ry2 = Math.floor(Math.random() * rows) * TILE;
        ctx.fillStyle = `rgba(0,245,255,${Math.random() * 0.05})`;
        ctx.fillRect(rx2 + 1, ry2 + 1, TILE - 2, TILE - 2);
      }
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, display: "block" }} />;
};

// ── MOBILE D-PAD ──────────────────────────────────────────────────────────────
const DPad = ({ onDirection }) => {
  const [pressed, setPressed] = useState(null);

  const handlePress = useCallback((dir) => {
    setPressed(dir);
    vibrate(18); // short crisp tap
    onDirection(dir);
  }, [onDirection]);

  const handleRelease = useCallback(() => {
    setPressed(null);
  }, []);

  const btn = (dir, label, style) => {
    const isActive = pressed === dir;
    return (
      <button
        onPointerDown={(e) => { e.preventDefault(); handlePress(dir); }}
        onPointerUp={handleRelease}
        onPointerLeave={handleRelease}
        style={{
          width: 56, height: 56,
          background: isActive
            ? "rgba(0,245,255,0.22)"
            : "rgba(0,0,0,0.55)",
          border: `1px solid ${isActive ? "#00f5ff" : "rgba(0,245,255,0.25)"}`,
          color: isActive ? "#00f5ff" : "rgba(0,245,255,0.55)",
          fontFamily: "monospace",
          fontSize: 18,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
          borderRadius: 4,
          boxShadow: isActive
            ? "0 0 16px rgba(0,245,255,0.5), inset 0 0 8px rgba(0,245,255,0.15)"
            : "0 0 6px rgba(0,0,0,0.6)",
          transition: "background 0.06s, border-color 0.06s, box-shadow 0.06s, color 0.06s",
          touchAction: "none",
          userSelect: "none",
          WebkitUserSelect: "none",
          ...style,
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      style={{
        position: "relative",
        width: 180,
        height: 180,
        display: "grid",
        gridTemplateColumns: "56px 56px 56px",
        gridTemplateRows: "56px 56px 56px",
        gap: 6,
        // Center grid contents
        placeItems: "center",
      }}
    >
      {/* Top row: empty, UP, empty */}
      <div />
      {btn("up", "▲", { gridColumn: 2, gridRow: 1 })}
      <div />
      {/* Middle row: LEFT, CENTER dot, RIGHT */}
      {btn("left", "◀", { gridColumn: 1, gridRow: 2 })}
      <div style={{
        width: 24, height: 24, borderRadius: "50%",
        background: "rgba(0,245,255,0.08)",
        border: "1px solid rgba(0,245,255,0.15)",
      }} />
      {btn("right", "▶", { gridColumn: 3, gridRow: 2 })}
      {/* Bottom row: empty, DOWN, empty */}
      <div />
      {btn("down", "▼", { gridColumn: 2, gridRow: 3 })}
      <div />
    </div>
  );
};

// ── SWIPE HANDLER (alternative to d-pad) ─────────────────────────────────────
function useSwipeControls(onDirection, active) {
  const touchStartRef = useRef(null);

  useEffect(() => {
    if (!active) return;

    const onTouchStart = (e) => {
      const t = e.touches[0];
      touchStartRef.current = { x: t.clientX, y: t.clientY };
    };

    const onTouchEnd = (e) => {
      if (!touchStartRef.current) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStartRef.current.x;
      const dy = t.clientY - touchStartRef.current.y;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      const MIN_SWIPE = 30;

      if (absDx < MIN_SWIPE && absDy < MIN_SWIPE) return;

      if (absDx > absDy) {
        onDirection(dx > 0 ? "right" : "left");
      } else {
        onDirection(dy > 0 ? "down" : "up");
      }
      touchStartRef.current = null;
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [active, onDirection]);
}

const SnakeGame = ({ onExit, storyLines = DEFAULT_STORY }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const audioCtxRef = useRef(null);

  const gridRef = useRef(getGrid(0)); // updated on game start once isMobile is known
  const snakeRef = useRef([]);
  const directionRef = useRef({ x: 1, y: 0 });
  const nextDirectionRef = useRef({ x: 1, y: 0 });
  const foodRef = useRef(null);
  const corruptedFoodRef = useRef(null);
  const wallsRef = useRef([]);

  const scoreRef = useRef(0);
  const bestScoreRef = useRef(parseInt(localStorage.getItem("neural-snake-best") || "0"));
  const ghostFramesRef = useRef(JSON.parse(localStorage.getItem("neural-snake-ghost") || "[]"));
  const currentRunFramesRef = useRef([]);

  const levelRef = useRef(1);
  const lastLevelScoreRef = useRef(0);
  const speedRef = useRef(6);
  const frameRef = useRef(0);
  const shakeRef = useRef(0);
  const glitchRef = useRef(0);
  const gameOverRef = useRef(false);

  const [gameState, setGameState] = useState("start");
  const [story, setStory] = useState(storyLines[0]);
  const [displayScore, setDisplayScore] = useState(0);
  const [displayBest, setDisplayBest] = useState(bestScoreRef.current);
  const [displayLevel, setDisplayLevel] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  // Detect touch device
  useEffect(() => {
    const check = () => setIsMobile("ontouchstart" in window || navigator.maxTouchPoints > 0);
    check();
  }, []);

  const playBeep = (freq, duration, type = "square") => {
    if (!audioCtxRef.current) return;
    try {
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type; osc.frequency.value = freq;
      osc.connect(gain); gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.start(); osc.stop(ctx.currentTime + duration);
    } catch (e) {}
  };

  const spawnFood = () => {
    const { TILES_X, TILES_Y } = gridRef.current;
    foodRef.current = { x: Math.floor(Math.random() * TILES_X), y: Math.floor(Math.random() * TILES_Y) };
    corruptedFoodRef.current = Math.random() < 0.25
      ? { x: Math.floor(Math.random() * TILES_X), y: Math.floor(Math.random() * TILES_Y) }
      : null;
  };

  const spawnWalls = () => {
    const { TILES_X, TILES_Y } = gridRef.current;
    wallsRef.current = [];
    for (let i = 0; i < levelRef.current; i++) {
      wallsRef.current.push({
        x: Math.floor(Math.random() * TILES_X),
        y: Math.floor(Math.random() * TILES_Y),
        speed: Math.random() > 0.5 ? 1 : -1
      });
    }
  };

  const crash = () => {
    if (gameOverRef.current) return;
    shakeRef.current = 25; glitchRef.current = 40;
    playBeep(120, 0.4, "sawtooth");
    // Strong crash vibration: three pulses
    vibrate([80, 40, 80, 40, 120]);
    if (scoreRef.current > bestScoreRef.current) {
      bestScoreRef.current = scoreRef.current;
      localStorage.setItem("neural-snake-best", scoreRef.current);
      localStorage.setItem("neural-snake-ghost", JSON.stringify(currentRunFramesRef.current));
      setDisplayBest(scoreRef.current);
    }
    gameOverRef.current = true;
    setTimeout(() => setGameState("over"), 400);
  };

  const startGame = () => {
    const { TILES_X, TILES_Y } = gridRef.current;
    snakeRef.current = [{ x: Math.floor(TILES_X / 2), y: Math.floor(TILES_Y / 2) }];
    directionRef.current = { x: 1, y: 0 };
    nextDirectionRef.current = { x: 1, y: 0 };
    scoreRef.current = 0; levelRef.current = 1; speedRef.current = 6;
    lastLevelScoreRef.current = 0; frameRef.current = 0;
    currentRunFramesRef.current = []; gameOverRef.current = false;
    shakeRef.current = 0; glitchRef.current = 0;
    spawnFood(); spawnWalls();
    setDisplayScore(0); setDisplayLevel(1);
    setStory(storyLines[0]);
    setGameState("playing");
  };

  // Unified direction handler (keyboard + touch + d-pad)
  const handleDirection = useCallback((dir) => {
    const map = {
      up:    { x: 0, y: -1 },
      down:  { x: 0, y:  1 },
      left:  { x: -1, y: 0 },
      right: { x:  1, y: 0 },
    };
    const newDir = map[dir];
    if (!newDir) return;
    const cur = directionRef.current;
    if (newDir.x !== -cur.x || newDir.y !== -cur.y) {
      nextDirectionRef.current = newDir;
    }
  }, []);

  // Swipe controls (canvas area swipe)
  useSwipeControls(handleDirection, gameState === "playing");

  // Game loop
  useEffect(() => {
    if (gameState !== "playing") return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const reservedBottom = isMobile ? dpadHeight : 0;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight - reservedBottom;
      gridRef.current = getGrid(reservedBottom);
    };
    resize();
    window.addEventListener("resize", resize);

    const keyDown = (e) => {
      if (e.key === "Escape" && onExit) onExit();
      const map = {
        ArrowUp: "up", w: "up",
        ArrowDown: "down", s: "down",
        ArrowLeft: "left", a: "left",
        ArrowRight: "right", d: "right",
      };
      if (map[e.key]) { e.preventDefault(); handleDirection(map[e.key]); }
    };
    window.addEventListener("keydown", keyDown);

    const draw = () => {
      const W = canvas.width; const H = canvas.height;
      const { tileSize, TILES_X, TILES_Y } = gridRef.current;
      ctx.save();
      if (shakeRef.current > 0) {
        ctx.translate((Math.random() - 0.5) * shakeRef.current, (Math.random() - 0.5) * shakeRef.current);
        shakeRef.current--;
      }
      ctx.fillStyle = "#000"; ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(0,245,255,0.04)"; ctx.lineWidth = 0.5;
      for (let i = 0; i <= TILES_X; i++) { ctx.beginPath(); ctx.moveTo(i * tileSize, 0); ctx.lineTo(i * tileSize, H); ctx.stroke(); }
      for (let j = 0; j <= TILES_Y; j++) { ctx.beginPath(); ctx.moveTo(0, j * tileSize); ctx.lineTo(W, j * tileSize); ctx.stroke(); }

      (ghostFramesRef.current[frameRef.current] || []).forEach((seg) => {
        ctx.fillStyle = "rgba(255,255,255,0.07)";
        ctx.fillRect(seg.x * tileSize + 1, seg.y * tileSize + 1, tileSize - 2, tileSize - 2);
      });

      ctx.shadowBlur = 12;
      snakeRef.current.forEach((seg, i) => {
        const isHead = i === 0;
        ctx.shadowColor = isHead ? "#00f5ff" : "#00ff88";
        ctx.fillStyle = isHead ? "#00f5ff" : "#00ff88";
        ctx.fillRect(seg.x * tileSize + 1, seg.y * tileSize + 1, tileSize - 2, tileSize - 2);
      });
      ctx.shadowBlur = 0;

      wallsRef.current.forEach((w) => {
        ctx.fillStyle = "#555"; ctx.shadowColor = "#888"; ctx.shadowBlur = 4;
        ctx.fillRect(w.x * tileSize + 1, w.y * tileSize + 1, tileSize - 2, tileSize - 2);
      });
      ctx.shadowBlur = 0;

      if (foodRef.current) {
        ctx.shadowColor = "#ff0055"; ctx.shadowBlur = 16; ctx.fillStyle = "#ff0055";
        ctx.fillRect(foodRef.current.x * tileSize + 1, foodRef.current.y * tileSize + 1, tileSize - 2, tileSize - 2);
        ctx.shadowBlur = 0;
      }
      if (corruptedFoodRef.current) {
        ctx.shadowColor = "#ff9900"; ctx.shadowBlur = 16; ctx.fillStyle = "#ff9900";
        ctx.fillRect(corruptedFoodRef.current.x * tileSize + 1, corruptedFoodRef.current.y * tileSize + 1, tileSize - 2, tileSize - 2);
        ctx.shadowBlur = 0;
      }

      if (glitchRef.current > 0) {
        ctx.fillStyle = `rgba(255,0,0,${Math.random() * 0.15})`;
        ctx.fillRect(0, 0, W, H);
        glitchRef.current--;
      }
      ctx.restore();
    };

    const update = () => {
      animationRef.current = requestAnimationFrame(update);
      if (gameOverRef.current) { draw(); return; }

      frameRef.current++;
      const interval = Math.max(4, Math.floor(60 / speedRef.current));
      if (frameRef.current % interval !== 0) return;

      directionRef.current = nextDirectionRef.current;
      const snake = snakeRef.current;
      const { TILES_X, TILES_Y } = gridRef.current;

      const head = {
        x: ((snake[0].x + directionRef.current.x) + TILES_X) % TILES_X,
        y: ((snake[0].y + directionRef.current.y) + TILES_Y) % TILES_Y
      };

      // Move walls BEFORE collision check
      wallsRef.current.forEach((w) => {
        w.x = ((w.x + w.speed) + TILES_X) % TILES_X;
      });

      const hitSelf = snake.some((s) => s.x === head.x && s.y === head.y);
      const hitWall = wallsRef.current.some((w) => w.x === head.x && w.y === head.y);
      if (hitSelf || hitWall) { crash(); draw(); return; }

      const wallCrushedBody = wallsRef.current.some((w) =>
        snake.some((s) => s.x === w.x && s.y === w.y)
      );
      if (wallCrushedBody) { crash(); draw(); return; }

      snake.unshift(head);
      let ate = false;

      if (foodRef.current && head.x === foodRef.current.x && head.y === foodRef.current.y) {
        scoreRef.current += 100; setDisplayScore(scoreRef.current);
        playBeep(700, 0.1, "triangle");
        vibrate(35); // satisfying eat pulse
        spawnFood(); ate = true;
      }
      if (corruptedFoodRef.current && head.x === corruptedFoodRef.current.x && head.y === corruptedFoodRef.current.y) {
        scoreRef.current += 50; setDisplayScore(scoreRef.current);
        playBeep(400, 0.15, "sawtooth");
        vibrate([20, 10, 20]); // glitchy double-pulse for corrupted food
        corruptedFoodRef.current = null; ate = true;
      }
      if (!ate) snake.pop();

      if (scoreRef.current >= 500 && scoreRef.current - lastLevelScoreRef.current >= 500) {
        lastLevelScoreRef.current = scoreRef.current;
        levelRef.current++; speedRef.current = Math.min(speedRef.current + 1, 20);
        spawnWalls(); shakeRef.current = 20; setDisplayLevel(levelRef.current);
        setStory(storyLines[Math.min(levelRef.current - 1, storyLines.length - 1)]);
        vibrate([40, 30, 40, 30, 60]); // level-up rumble
      }

      currentRunFramesRef.current.push(snake.map((seg) => ({ ...seg })));
      draw();
    };

    update();

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("keydown", keyDown);
    };
  }, [gameState, handleDirection]);

  // How much bottom padding to leave for the d-pad on mobile
  const dpadHeight = 200;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000", overflow: "hidden" }}>

      <canvas
        ref={canvasRef}
        style={{
          position: "absolute", top: 0, left: 0, display: "block",
          opacity: gameState === "playing" || gameState === "over" ? 1 : 0,
        }}
      />

      {/* ── START SCREEN ── */}
      <AnimatePresence>
        {gameState === "start" && (
          <motion.div
            key="start"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.3 } }}
            style={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              fontFamily: "monospace", color: "#fff",
            }}
          >
            <GridBackground />
            <div className="absolute inset-0 pointer-events-none" />
            <div className="absolute top-1/4 left-1/3 w-[600px] h-[600px] rounded-full pointer-events-none hidden dark:block"
              style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.07) 0%, transparent 70%)' }} />
            <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none hidden dark:block"
              style={{ background: 'radial-gradient(circle, hsl(var(--secondary) / 0.05) 0%, transparent 70%)' }} />
            <div className="absolute right-[15%] top-1/2 w-[450px] h-[450px] rounded-full pointer-events-none block dark:hidden"
              style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.08) 0%, transparent 65%)', transform: 'translateY(-50%)' }} />
            <div className="absolute inset-0 bg-grid pointer-events-none dark:opacity-40 opacity-15" />
            <div className="absolute inset-0"><ParticleField /></div>

            <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "0 24px" }}>
              <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <div style={{ fontSize: 11, letterSpacing: 10, color: "#00ff88", opacity: 0.5, marginBottom: 10, textShadow: "0 0 10px #00ff88" }}>
                  SYSTEM / ARCADE
                </div>
                <div style={{ fontSize: 36, letterSpacing: 10, color: "#fff", fontWeight: 900, textShadow: "0 0 30px rgba(0, 255, 34, 0.5)" }}>
                  SYNAPSE SNAKE
                </div>
                <motion.div
                  initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.4, duration: 0.6 }}
                  style={{ height: 1, background: "linear-gradient(90deg, transparent, #00ff88, transparent)", marginTop: 12, transformOrigin: "center" }}
                />
                <div style={{ fontSize: 10, letterSpacing: 3, color: "rgba(255,255,255,0.5)", marginTop: 12 }}>
                  NAVIGATE THE NEURAL GRID.
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                style={{ marginTop: 32, display: "inline-block", textAlign: "left" }}
              >
                {(isMobile ? [
                  ["MOVE", "D-PAD / SWIPE"],
                  ["WRAP", "EDGES LOOP"],
                  ["RED", "+100 PTS"],
                  ["ORANGE", "+50 PTS"],
                  ["EXIT", "BACK BUTTON"],
                ] : [
                  ["MOVE", "WASD / ARROWS"],
                  ["WRAP", "EDGES LOOP"],
                  ["RED", "+100 PTS"],
                  ["ORANGE", "+50 PTS"],
                  ["EXIT", "ESC"],
                ]).map(([label, val]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 40, fontSize: 10, letterSpacing: 2, marginBottom: 8 }}>
                    <span style={{ color: "rgba(255,255,255,0.5)" }}>{label}</span>
                    <span style={{ color: "#00ff88" }}>{val}</span>
                  </div>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
                style={{ marginTop: 36, display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}
              >
                <button
                  onClick={startGame}
                  style={{
                    padding: "14px 40px", background: "transparent",
                    border: "1px solid #00ff88", color: "#00ff88",
                    fontFamily: "monospace", fontSize: 13, letterSpacing: 4,
                    cursor: "pointer", textShadow: "0 0 8px #00ff88",
                    boxShadow: "0 0 16px rgba(0, 255, 34, 0.3)",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,245,255,0.08)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  ▶ NAVIGATE
                </button>

                {bestScoreRef.current > 0 && (
                  <div style={{ fontSize: 10, letterSpacing: 3, color: "rgba(255,255,255,0.5)" }}>
                    BEST: {bestScoreRef.current}
                  </div>
                )}

                <button
                  onClick={onExit}
                  style={{ background: "transparent", border: "none", fontFamily: "monospace", fontSize: 10, letterSpacing: 4, color: "rgba(255,255,255,0.25)", cursor: "pointer", marginTop: 4 }}
                  onMouseEnter={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.7)"}
                  onMouseLeave={(e) => e.currentTarget.style.color = "rgba(255,255,255,0.5)"}
                >
                  ← BACK
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── LIVE HUD ── */}
      {gameState === "playing" && (
        <div style={{
          position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)",
          textAlign: "center", color: "#fff", fontFamily: "monospace", pointerEvents: "none",
          textShadow: "0 0 10px #00ff88", whiteSpace: "nowrap", zIndex: 10,
        }}>
          <div style={{ fontSize: 10, letterSpacing: 6, opacity: 0.5, color: "#00ff88" }}>SYNAPSE SNAKE</div>
          <div style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>{story}</div>
          <div style={{ marginTop: 6, fontSize: 11, opacity: 0.5 }}>
            SCORE: {displayScore} &nbsp;|&nbsp; BEST: {displayBest} &nbsp;|&nbsp; LVL: {displayLevel}
          </div>
        </div>
      )}

      {/* Keyboard hint — desktop only */}
      {gameState === "playing" && !isMobile && (
        <div style={{
          position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
          color: "rgba(255,255,255,0.2)", fontSize: 10, fontFamily: "monospace", letterSpacing: 3,
          pointerEvents: "none", whiteSpace: "nowrap"
        }}>
          ARROW KEYS / WASD &nbsp;·&nbsp; ESC TO EXIT
        </div>
      )}

      {/* ── MOBILE D-PAD + EXIT ── */}
      {gameState === "playing" && isMobile && (
        <div style={{
          position: "absolute",
          bottom: 0, left: 0, right: 0,
          height: dpadHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 28px 16px 28px",
          background: "linear-gradient(to top, rgba(0,0,0,0.92) 60%, transparent)",
          zIndex: 20,
          touchAction: "none",
        }}>
          {/* Swipe hint */}
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
            opacity: 0.25, pointerEvents: "none",
          }}>
            <div style={{ fontSize: 8, letterSpacing: 2, color: "#00f5ff", fontFamily: "monospace" }}>SWIPE</div>
            <div style={{ fontSize: 8, letterSpacing: 2, color: "#00f5ff", fontFamily: "monospace" }}>CANVAS</div>
            <div style={{ fontSize: 10, color: "#00f5ff", fontFamily: "monospace" }}>↕↔</div>
          </div>

          {/* D-Pad */}
          <DPad onDirection={handleDirection} />

          {/* Exit button */}
          <button
            onPointerDown={(e) => { e.preventDefault(); vibrate(30); if (onExit) onExit(); }}
            style={{
              width: 56, height: 56,
              background: "rgba(0,0,0,0.55)",
              border: "1px solid rgba(255,0,85,0.3)",
              color: "rgba(255,0,85,0.5)",
              fontFamily: "monospace", fontSize: 9, letterSpacing: 1,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
              cursor: "pointer", borderRadius: 4,
              boxShadow: "0 0 6px rgba(0,0,0,0.6)",
              touchAction: "none",
              userSelect: "none",
              WebkitUserSelect: "none",
            }}
          >
            <span style={{ fontSize: 14 }}>✕</span>
            <span>EXIT</span>
          </button>
        </div>
      )}

      {/* ── GAME OVER ── */}
      <AnimatePresence>
        {gameState === "over" && (
          <motion.div
            key="gameover"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: "absolute", inset: 0, background: "rgba(0,0,0,0.88)",
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", color: "#fff", fontFamily: "monospace",
              zIndex: 30,
            }}
          >
            <motion.h1
              initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              style={{ fontSize: 36, marginBottom: 8, color: "#ff0055", textShadow: "0 0 24px #ff0055", letterSpacing: 6 }}
            >
              SYSTEM FAILURE
            </motion.h1>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} style={{ textAlign: "center" }}>
              <p style={{ opacity: 0.5, fontSize: 11, letterSpacing: 3, marginBottom: 6 }}>FINAL SCORE</p>
              <p style={{ fontSize: 40, letterSpacing: 4, color: "#fff", marginBottom: 4 }}>{scoreRef.current}</p>
              <p style={{ opacity: 0.3, fontSize: 10, letterSpacing: 3, marginBottom: 16 }}>LEVEL {levelRef.current}</p>

              {scoreRef.current >= bestScoreRef.current && scoreRef.current > 0 && (
                <motion.p
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  style={{ color: "#00f5ff", fontSize: 11, letterSpacing: 4, textShadow: "0 0 10px #00f5ff", marginBottom: 20 }}
                >
                  ◆ NEW BEST ◆
                </motion.p>
              )}

              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={startGame}
                  style={{
                    padding: "12px 28px", background: "transparent", border: "1px solid #00f5ff",
                    color: "#00f5ff", fontFamily: "monospace", fontSize: 12, letterSpacing: 3,
                    cursor: "pointer", textShadow: "0 0 8px #00f5ff", boxShadow: "0 0 12px rgba(0,245,255,0.25)",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,245,255,0.08)"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  RESTART
                </button>
                {onExit && (
                  <button
                    onClick={onExit}
                    style={{
                      padding: "12px 28px", background: "transparent", border: "1px solid #ff0055",
                      color: "#ff0055", fontFamily: "monospace", fontSize: 12, letterSpacing: 3,
                      cursor: "pointer", textShadow: "0 0 8px #ff0055", boxShadow: "0 0 12px rgba(255,0,85,0.25)",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,0,85,0.08)"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    EXIT
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SnakeGame;